import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";

type Todo = {
  id: number;
  text: string;
  author: string | null;
  done: number; // 0 | 1
  createdAt: number;
};

const AUTHOR_KEY = "todo-author";

// Internal build scratchpad. Not a page of its own — rendered as a tab inside
// /admin, behind the admin password. Delete this and its API before the site
// goes live.
export function Todos() {
  const [todos, setTodos] = useState<Todo[]>([]);
  const [text, setText] = useState("");
  // Remembered per-browser so you only type your name once.
  const [name, setName] = useState(
    () => localStorage.getItem(AUTHOR_KEY) ?? "",
  );
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      const r = await api.get<{ todos: Todo[] }>("/todos");
      setTodos(r.todos);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load todos.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const add = async () => {
    const value = text.trim();
    if (!value || busy) return;
    const author = name.trim();
    setBusy(true);
    try {
      const r = await api.post<{ todo: Todo }>("/todos", {
        text: value,
        author: author || undefined,
      });
      setTodos((t) => [r.todo, ...t]);
      setText("");
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not add.");
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (id: number) => {
    // Optimistic — flip locally, reconcile on response.
    setTodos((t) =>
      t.map((x) => (x.id === id ? { ...x, done: x.done ? 0 : 1 } : x)),
    );
    try {
      await api.post(`/todos/${id}/toggle`);
    } catch {
      void load();
    }
  };

  const remove = async (id: number) => {
    const prev = todos;
    setTodos((t) => t.filter((x) => x.id !== id));
    try {
      await api.del(`/todos/${id}`);
    } catch {
      setTodos(prev);
    }
  };

  const open = useMemo(() => todos.filter((t) => !t.done), [todos]);
  const done = useMemo(() => todos.filter((t) => t.done), [todos]);

  return (
    <div className="mx-auto max-w-2xl pb-16">
      <div className="pt-2">
        <h1 className="font-serif text-5xl text-pine sm:text-6xl">To-do</h1>
        <p className="mt-3 text-lg text-pine/65">
          this is the work queue for stuff on the site and will be deleted
          before we send it to anyone.
        </p>
      </div>

      <form
        className="mt-10 flex items-stretch border-b border-pine/25 focus-within:border-midnight-teal"
        onSubmit={(e) => {
          e.preventDefault();
          void add();
        }}
      >
        <input
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            localStorage.setItem(AUTHOR_KEY, e.target.value);
          }}
          placeholder="name"
          aria-label="Your name (optional)"
          className="w-20 shrink-0 bg-transparent py-2 text-xl text-pine outline-none placeholder:italic placeholder:text-pine/35 sm:w-28"
        />
        <span aria-hidden="true" className="mx-3 my-1 w-px bg-pine/20" />
        <input
          autoFocus
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="add something to work on…"
          className="min-w-0 flex-1 bg-transparent py-2 text-xl text-pine outline-none placeholder:italic placeholder:text-pine/35"
        />
        <button
          type="submit"
          disabled={!text.trim() || busy}
          className="ml-4 shrink-0 self-center text-sm uppercase tracking-[0.2em] text-midnight-teal transition-colors hover:text-pine disabled:cursor-not-allowed disabled:opacity-30"
        >
          Add
        </button>
      </form>

      {error && <p className="mt-4 text-base text-dusty-rose">{error}</p>}

      {loading ? (
        <p className="mt-10 text-pine/40">…</p>
      ) : todos.length === 0 ? (
        <p className="mt-10 italic text-pine/50">
          Nothing yet. Add the first thing above.
        </p>
      ) : (
        <>
          <ul className="mt-8 divide-y divide-pine/10 border-t border-pine/10">
            {open.map((t) => (
              <TodoRow
                key={t.id}
                todo={t}
                onToggle={() => void toggle(t.id)}
                onDelete={() => void remove(t.id)}
              />
            ))}
          </ul>

          {done.length > 0 && (
            <div className="mt-12">
              <p className="text-sm uppercase tracking-[0.2em] text-pine/40">
                Done ({done.length})
              </p>
              <ul className="mt-3 divide-y divide-pine/10 border-t border-pine/10">
                {done.map((t) => (
                  <TodoRow
                    key={t.id}
                    todo={t}
                    onToggle={() => void toggle(t.id)}
                    onDelete={() => void remove(t.id)}
                  />
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function TodoRow({
  todo,
  onToggle,
  onDelete,
}: {
  todo: Todo;
  onToggle: () => void;
  onDelete: () => void;
}) {
  const done = !!todo.done;
  return (
    <li className="group flex items-center gap-4 py-4">
      <button
        type="button"
        onClick={onToggle}
        aria-pressed={done}
        aria-label={done ? "Mark as not done" : "Mark as done"}
        className={`flex h-5 w-5 shrink-0 items-center justify-center border transition-colors ${
          done
            ? "border-midnight-teal bg-midnight-teal text-cream"
            : "border-pine/40 text-transparent hover:border-midnight-teal"
        }`}
      >
        <svg viewBox="0 0 16 16" className="h-3 w-3" aria-hidden="true">
          <path
            d="M3 8.5l3 3 7-7"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <span className="flex-1 text-lg">
        <span className={done ? "text-pine/40 line-through" : "text-pine"}>
          {todo.text}
        </span>
        {todo.author && (
          <span className="ml-2 whitespace-nowrap text-sm italic text-pine/40">
            — {todo.author}
          </span>
        )}
      </span>

      <button
        type="button"
        onClick={onDelete}
        aria-label="Delete"
        className="shrink-0 px-1 text-xl leading-none text-pine/25 opacity-0 transition-opacity hover:text-dusty-rose focus-visible:opacity-100 group-hover:opacity-100"
      >
        &times;
      </button>
    </li>
  );
}
